import { cn, Input } from '@repo/ui'
import { useEffect, useState } from 'react'

import {
  type DurationSegment,
  type DurationUnit,
  formatDuration,
  segmentsToStore,
  type SegmentValues,
  storeToSegments,
} from '@/lib/duration'

const SEGMENT_LABEL: Record<DurationSegment, string> = {
  days: 'dias',
  hours: 'h',
  minutes: 'min',
  seconds: 's',
}

const SECONDS_PER: Record<DurationSegment, number> = {
  days: 86400,
  hours: 3600,
  minutes: 60,
  seconds: 1,
}

interface Props {
  /** valor na unidade de armazenamento (storeUnit); null quando vazio */
  value: number | null | undefined
  onChange: (value: number | null) => void
  /** unidade em que o valor é gravado/emitido */
  storeUnit: DurationUnit
  /** quais caixas mostrar, do maior para o menor */
  segments: DurationSegment[]
  /** limites em storeUnit */
  min?: number
  max?: number
  /** se true, todas as caixas vazias emitem null */
  allowEmpty?: boolean
  disabled?: boolean
  className?: string
  /** id da primeira caixa (para <label htmlFor>) */
  id?: string
}

type StringParts = Partial<Record<DurationSegment, string>>

function clamp(value: number, min?: number, max?: number): number {
  let next = value
  if (typeof min === 'number') next = Math.max(min, next)
  if (typeof max === 'number') next = Math.min(max, next)
  return next
}

function toNumeric(parts: StringParts): SegmentValues {
  const out: SegmentValues = {}
  for (const key of Object.keys(parts) as DurationSegment[]) {
    out[key] = Math.max(0, Math.floor(Number(parts[key]) || 0))
  }
  return out
}

function toStringParts(parts: SegmentValues): StringParts {
  const out: StringParts = {}
  for (const key of Object.keys(parts) as DurationSegment[]) {
    out[key] = String(parts[key] ?? 0)
  }
  return out
}

function isAllEmpty(parts: StringParts, segments: DurationSegment[]): boolean {
  return segments.every((seg) => (parts[seg] ?? '') === '')
}

/**
 * Input composto de duração: uma caixinha numérica por segmento
 * (dias/horas/min/seg) com resumo legível ao lado. Mantém um buffer local
 * para não recolapsar os campos enquanto o usuário digita.
 */
export function DurationInput({
  value,
  onChange,
  storeUnit,
  segments,
  min,
  max,
  allowEmpty = false,
  disabled = false,
  className,
  id,
}: Props) {
  const segKey = segments.join('|')

  const [parts, setParts] = useState<StringParts>(() =>
    value == null && allowEmpty
      ? {}
      : toStringParts(storeToSegments(value ?? 0, storeUnit, segments))
  )

  // Re-sincroniza quando o valor muda por fora (reset/troca de registro),
  // sem clobber enquanto o usuário digita (parts já representam o value).
  useEffect(() => {
    const represented = isAllEmpty(parts, segments)
      ? null
      : segmentsToStore(toNumeric(parts), storeUnit)
    if (value == null) {
      if (allowEmpty) {
        if (!isAllEmpty(parts, segments)) setParts({})
      } else if (represented !== 0) {
        setParts(toStringParts(storeToSegments(0, storeUnit, segments)))
      }
      return
    }
    if (represented !== value) {
      setParts(toStringParts(storeToSegments(value, storeUnit, segments)))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, storeUnit, segKey, allowEmpty])

  function update(segment: DurationSegment, raw: string) {
    const next: StringParts = { ...parts, [segment]: raw }
    setParts(next)
    if (allowEmpty && isAllEmpty(next, segments)) {
      onChange(null)
      return
    }
    onChange(clamp(segmentsToStore(toNumeric(next), storeUnit), min, max))
  }

  const summary =
    allowEmpty && isAllEmpty(parts, segments)
      ? '—'
      : formatDuration(
          segmentsToStore(toNumeric(parts), storeUnit),
          storeUnit,
          segments
        )

  const orderedSegments = [...segments].sort(
    (a, b) => SECONDS_PER[b] - SECONDS_PER[a]
  )

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {orderedSegments.map((segment, index) => (
        <div key={segment} className="flex items-center gap-1">
          <Input
            id={index === 0 ? id : undefined}
            type="number"
            min={0}
            inputMode="numeric"
            disabled={disabled}
            value={parts[segment] ?? ''}
            onChange={(event) => update(segment, event.target.value)}
            className="w-16"
          />
          <span className="text-muted-foreground text-xs">
            {SEGMENT_LABEL[segment]}
          </span>
        </div>
      ))}
      <span className="text-muted-foreground ml-1 text-xs">= {summary}</span>
    </div>
  )
}
